import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { tutorService } from '../services/tutorService';
import { toast } from 'sonner';

const SkillTest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tests, setTests] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentQuestions, setCurrentQuestions] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(1800); // 30 minutes total

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      
      // The backend now automatically determines the required tests 
      // based on the tutor's profile subjects/classes and the threshold logic.
      const response = await tutorService.getTestQuestions();
      
      console.log('Tests received:', response.data.tests);

      setTests(response.data.tests);

      // Initialize current question index for each test
      const initialQuestions = {};
      response.data.tests.forEach((_, index) => {
        initialQuestions[index] = 0;
      });
      setCurrentQuestions(initialQuestions);

      setLoading(false);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load test questions';
      toast.error(message);
      console.error('Load questions error:', error);
      navigate('/tutor/dashboard');
    }
  };

  // Timer
  useEffect(() => {
    if (loading || submitting || tests.length === 0) return;

    if (timeRemaining > 0) {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0) {
      handleSubmitAll();
    }
  }, [timeRemaining, loading, submitting, tests.length]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (testIndex, questionId, answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [`${testIndex}-${questionId}`]: answerIndex
    });
  };

  const handleNext = (testIndex) => {
    const test = tests[testIndex];
    if (currentQuestions[testIndex] < test.questions.length - 1) {
      setCurrentQuestions({
        ...currentQuestions,
        [testIndex]: currentQuestions[testIndex] + 1
      });
    }
  };

  const handlePrevious = (testIndex) => {
    if (currentQuestions[testIndex] > 0) {
      setCurrentQuestions({
        ...currentQuestions,
        [testIndex]: currentQuestions[testIndex] - 1
      });
    }
  };

  const isTestComplete = (testIndex) => {
    const test = tests[testIndex];
    let answeredCount = 0;
    test.questions.forEach(q => {
      if (selectedAnswers[`${testIndex}-${q.id}`] !== undefined) {
        answeredCount++;
      }
    });
    return answeredCount === test.questions.length;
  };

  const allTestsComplete = () => {
    return tests.every((_, index) => isTestComplete(index));
  };

  const handleSubmitAll = async () => {
    if (!allTestsComplete()) {
      toast.error('Please answer all questions in all tests before submitting');
      return;
    }

    setSubmitting(true);
    try {
      let totalScore = 0;
      let allPassed = true;

      // Submit each test
      for (let testIndex = 0; testIndex < tests.length; testIndex++) {
        const test = tests[testIndex];
        const answers = test.questions.map(q => ({
          question_id: q.id,
          selected_answer: q.options[selectedAnswers[`${testIndex}-${q.id}`]]
        }));

        const response = await tutorService.submitTest(test.test_id, answers);
        totalScore += response.data.score;

        if (!response.data.passed) {
          allPassed = false;
        }
      }

      const avgScore = totalScore / tests.length;

      // Always show under review message (admin will evaluate)
      toast.success('All tests submitted successfully!');
      toast.info('Your profile is now under review. You will be notified once approved.');

      navigate('/tutor/dashboard');
    } catch (error) {
      toast.error('Failed to submit tests');
      console.error('Submit test error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <div className="max-w-6xl mx-auto">
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">No Questions Available</h2>
          <p className="text-gray-600 mb-4">Please contact support.</p>
          <Button onClick={() => navigate('/tutor/dashboard')}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const getTabProgress = (testIndex) => {
    const test = tests[testIndex];
    let answeredCount = 0;
    test.questions.forEach(q => {
      if (selectedAnswers[`${testIndex}-${q.id}`] !== undefined) {
        answeredCount++;
      }
    });
    return (answeredCount / test.questions.length) * 100;
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Timer Banner */}
      <div className="bg-gradient-to-r from-blue-500 to-teal-500 rounded-xl p-6 mb-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-1">Skill Assessment</h2>
            <p className="text-blue-100">Complete all tests to activate your profile</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
            <div className="flex items-center space-x-2">
              <Clock className="h-6 w-6" />
              <span className="text-2xl font-bold">{formatTime(timeRemaining)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs for Each Subject */}
      <Card className="p-6 bg-white shadow-xl border-gray-100">
        <Tabs value={currentTab.toString()} onValueChange={(v) => setCurrentTab(parseInt(v))}>
          <TabsList className="grid w-full mb-6" style={{ gridTemplateColumns: `repeat(${tests.length}, 1fr)` }}>
            {tests.map((test, index) => (
              <TabsTrigger
                key={index}
                value={index.toString()}
                className="relative"
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">{test.subject}</span>
                  <span className="text-xs text-gray-500">{Math.round(getTabProgress(index))}%</span>
                </div>
                {isTestComplete(index) && (
                  <CheckCircle className="absolute -top-1 -right-1 h-4 w-4 text-green-600" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {tests.map((test, testIndex) => {
            const currentQ = currentQuestions[testIndex] || 0;
            const question = test.questions[currentQ];
            const progress = ((currentQ + 1) / test.questions.length) * 100;

            return (
              <TabsContent key={testIndex} value={testIndex.toString()}>
                {/* Progress */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      Question {currentQ + 1} of {test.questions.length}
                    </span>
                    <span className="text-sm font-medium text-blue-600">
                      {Math.round(progress)}% Complete
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-teal-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Question */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{question.question}</h3>

                  {/* Options */}
                  <div className="space-y-3">
                    {Array.isArray(question.options) ? question.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(testIndex, question.id, index)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${selectedAnswers[`${testIndex}-${question.id}`] === index
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${selectedAnswers[`${testIndex}-${question.id}`] === index
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300'
                              }`}
                          >
                            {selectedAnswers[`${testIndex}-${question.id}`] === index && (
                              <CheckCircle className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <span className="text-gray-900">{option}</span>
                        </div>
                      </button>
                    )) : (
                      <p className="text-red-500">Error: Options not found for this question.</p>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                  <Button
                    onClick={() => handlePrevious(testIndex)}
                    disabled={currentQ === 0 || submitting}
                    variant="outline"
                    className="border-gray-300 disabled:opacity-50"
                  >
                    Previous
                  </Button>

                  {currentQ === test.questions.length - 1 ? (
                    allTestsComplete() ? (
                      <Button
                        onClick={() => {
                          window.scrollTo({
                            top: document.documentElement.scrollHeight,
                            behavior: 'smooth'
                          });
                        }}
                        disabled={submitting}
                        className="bg-gradient-to-r from-green-600 to-teal-500 hover:from-green-700 hover:to-teal-600 text-white animate-pulse"
                      >
                        Go to Submit ↓
                      </Button>
                    ) : testIndex < tests.length - 1 ? (
                      <Button
                        onClick={() => setCurrentTab(testIndex + 1)}
                        disabled={submitting}
                        className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white"
                      >
                        Next Test →
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setCurrentTab(0)}
                        disabled={submitting}
                        className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white"
                      >
                        Review All Tests
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={() => handleNext(testIndex)}
                      disabled={submitting}
                      className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white"
                    >
                      Next Question
                    </Button>
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Test Summary */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Test Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {tests.map((test, index) => {
              const progress = getTabProgress(index);
              const isComplete = isTestComplete(index);
              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${isComplete
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-gray-50'
                    }`}
                  onClick={() => setCurrentTab(index)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{test.subject}</span>
                    {isComplete && <CheckCircle className="h-5 w-5 text-green-600" />}
                  </div>
                  <div className="text-xs text-gray-600">
                    {Math.round(progress)}% Complete
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div
                      className={`h-1.5 rounded-full ${isComplete ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Important Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Important:</p>
                <ul className="space-y-1 text-blue-800">
                  <li>• Your tests will be reviewed by our admin team</li>
                  <li>• Review typically takes 24-48 hours</li>
                  <li>• You'll be notified via email once approved</li>
                  <li>• Make sure all answers are correct before submitting</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmitAll}
            disabled={submitting || !allTestsComplete()}
            className="w-full bg-gradient-to-r from-green-600 to-teal-500 hover:from-green-700 hover:to-teal-600 text-white h-12 text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
          >
            {submitting ? 'Submitting All Tests...' : allTestsComplete() ? 'Submit All Tests for Review' : 'Complete All Tests First'}
          </Button>
          {!allTestsComplete() && (
            <p className="text-center text-sm text-gray-600 mt-2">
              Please answer all questions in all tabs before submitting
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SkillTest;
